<script setup lang="ts">
import { useSiteStore } from '~/stores/site.store';

interface ICarouselProps {
  title: string;
  id: string;
  route?: string;
  displayShowAll?: boolean;
  verticalNavigation?: boolean;
  titleClass?: string;
  displayArrows?: boolean;
  displayTitle?: boolean;
}

const props = withDefaults(defineProps<ICarouselProps>(), {
  title: '',
  route: '',
  displayShowAll: true,
  verticalNavigation: false,
  titleClass: 'text-base',
  displayArrows: true,
  displayTitle: true,
});

const carouselId = computed<string>(() => `${props.id.toLowerCase()}-carousel`);

const router = useRouter();
const site = useSiteStore();
const carousel = ref<HTMLDivElement>(null);
const displayTooltip = ref<boolean>(
  site.getFeatureFlags.get('website.enabletitletooltips'),
);

function navigate() {
  if (!props.route) return;
  router.push(props.route);
}

let startX = 0;
let startY = 0;
let scrollLeft = 0;
let isDragging = false;

const isDown = ref(false);
const isItemsPointerEventsRemoved = ref(false);

const SCROLL_SPEED_FACTOR = 2;

const removeAddItemsPointerEvents = (attr: 'none' | 'auto') => {
  const items = carousel.value?.getElementsByClassName('is-carousel');
  if (items) {
    for (let i = 0; i < items.length; i++) {
      (items[i] as HTMLElement).style.pointerEvents = attr;
    }
  }
  isItemsPointerEventsRemoved.value = attr === 'none';
};

const startScroll = (x: number, y: number) => {
  setTimeout(() => {
    isDown.value = true;
    startX = x;
    startY = y;
    scrollLeft = carousel.value?.scrollLeft || 0;
    isDragging = false;
  }, 100);
};

const endScroll = (e: MouseEvent | TouchEvent) => {
  isDown.value = false;
  if (isItemsPointerEventsRemoved.value) {
    e.preventDefault();
    e.stopPropagation();
    removeAddItemsPointerEvents('auto');
  }
};

const moveScroll = (
  currentX: number,
  currentY: number,
  e: TouchEvent | MouseEvent,
) => {
  if (!isDown.value) return;

  const deltaX = currentX - startX;
  const deltaY = currentY - startY;

  if (!isDragging && Math.abs(deltaY) > Math.abs(deltaX)) {
    isDown.value = false;
    return;
  }

  isDragging = true;

  e.preventDefault();
  e.stopPropagation();

  if (!isItemsPointerEventsRemoved.value) removeAddItemsPointerEvents('none');

  const walkX = deltaX * SCROLL_SPEED_FACTOR;
  const newScrollLeft = scrollLeft - walkX;

  if (carousel.value) {
    carousel.value.scrollLeft = Math.max(
      0,
      Math.min(
        carousel.value.scrollWidth - carousel.value.clientWidth,
        newScrollLeft,
      ),
    );
  }
};

const mouseIsDown = (e: MouseEvent) => {
  e.preventDefault();
  startScroll(e.pageX, e.pageY);
};
const mouseMove = (e: MouseEvent) => {
  moveScroll(e.pageX, e.pageY, e);
};

const touchStart = (e: TouchEvent) => {
  startScroll(e.touches[0].pageX, e.touches[0].pageY);
};
const touchMove = (e: TouchEvent) => {
  moveScroll(e.touches[0].pageX, e.touches[0].pageY, e);
};
</script>

<template>
  <div
    :class="`container md:mx-auto mx-0 px-0 ${
      isDown ? 'cursor-grabbing' : 'cursor-grab'
    }`"
  >
    <div
      v-if="displayTitle"
      class="text-base flex justify-between items-center ml-2 pb-2 pt-7 md:mx-0"
    >
      <div
        class="font-bold truncate capitalize flex items-center text-xl"
        :class="props.titleClass"
      >
        <slot name="icon" />
        {{ props.title }}
        <slot name="subtitle" />
        <LazyDesignSystemTooltipModal
          v-if="displayTooltip"
          :title="props.title"
          :route="props.route"
        />
      </div>
      <div class="gap-1 items-center flex">
        <DesignSystemButton
          v-if="
            props.route && props.displayShowAll && !props.verticalNavigation
          "
          class="text-sm"
          type="transparent"
          padding="sm"
          @click="navigate()"
        >
          {{ $t('show-all') }}
        </DesignSystemButton>

        <DesignSystemButton
          v-if="props.route && props.displayShowAll && props.verticalNavigation"
          class="text-sm gap-1"
          type="transparent"
          padding="sm"
          @click="navigate()"
        >
          {{ $t('all-games') }}
          <slot name="prefix-arrows" />
        </DesignSystemButton>
      </div>
    </div>
    <div
      :id="carouselId"
      ref="carousel"
      class="space-x-2 md:space-x-4 flex overflow-x-auto snap-both pb-2"
      @mousedown="mouseIsDown($event)"
      @mouseup="endScroll($event)"
      @mouseleave="endScroll($event)"
      @mousemove="mouseMove($event)"
      @touchstart="touchStart($event)"
      @touchend="endScroll($event)"
      @touchcancel="endScroll($event)"
      @touchmove="touchMove($event)"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
::selection {
  background: transparent;
}
::-moz-selection {
  background: transparent;
}
</style>
